import React, { memo, useContext, useRef } from 'react'
import PropTypes from 'prop-types'

import { Icon, Typography, Button } from '../index'
import { LanguageContext } from '../../../context/LanguageContext'
import { util } from '../../../util'
import _ from 'lodash'

/** This Component is responsible to render input text field
 * @returns {*} Custom Input Component
 */
const InputField = ({
    className,
    clearInputHandler,
    error,
    height,
    inputLabelMargin,
    inputProps,
    isFullWidth,
    isModified,
    isOptional,
    label,
    maxLength,
    readOnly,
    showClear = false,
    showInputCount = false,
    size,
    value,
    viewOnly,
    ...rest
}) => {
    //Localization
    const { rtl, strings } = useContext(LanguageContext)

    const inputValue = _.isNil(value) ? '' : value

    const focusRef = useRef()
    const focusInput = () => focusRef.current.focus()

    const inputContainerClass = util.joinClasses(
        'input-container',
        size,
        height && `height-${height}`,
        isFullWidth && 'is-full-width',
        showInputCount && 'input-container__show-count',
        className
    )

    /**
     * @param position (start | end)
     * @returns {*} Icon Component
     */
    const renderInputProps = position => {
        if (viewOnly) {
            return null
        }
        if (inputProps && inputProps.hasOwnProperty(position)) {
            return (
                <i className={util.joinClasses(`input__${position}`, inputProps.type)}>
                    <Icon
                        onClick={focusInput}
                        size={inputProps.hasOwnProperty('size') ? inputProps['size'] : 'sm'}
                        display={'inline'}
                        renderIcon={inputProps[position]}
                        className={
                            inputProps.hasOwnProperty('className') && inputProps['className']
                        }
                        readOnly={readOnly}
                    />
                </i>
            )
        }
    }

    const renderClearButton = () =>
        showClear ? (
            <Button
                color={'transparent'}
                size={'min-width'}
                className={inputValue === '' ? 'input__clear__hide' : 'input__clear__show'}
                onClick={clearInputHandler}
            >
                {strings.CLEAR}
            </Button>
        ) : null

    const renderInputControl = () => (
        <>
            <input
                data-testid={'input_field'}
                ref={focusRef}
                className={util.joinClasses(
                    'input__control',
                    readOnly && 'read-only',
                    (inputValue || typeof inputValue === 'number') && 'has-value',
                    !!inputLabelMargin && 'label-margin',
                    isModified && 'modified'
                )}
                value={inputValue}
                autoComplete={'off'}
                maxLength={maxLength}
                readOnly={readOnly || viewOnly}
                {...rest}
            />
            <span className={util.joinClasses('label', rtl && 'rtl')} onClick={focusInput}>
                {label}
            </span>
        </>
    )

    return (
        <div className={inputContainerClass} onClick={focusInput}>
            <div
                className={util.joinClasses(
                    'input',
                    error && error.length && 'error',
                    viewOnly && 'view-only',
                    isModified && 'modified'
                )}
            >
                {renderInputProps('start')}
                {renderInputControl()}
                {renderInputProps('end')}
                {renderClearButton()}
            </div>
            <Typography
                className={'mt-4'}
                show={showInputCount}
                align={'right'}
                text={inputValue ? `${inputValue.length} / ${maxLength}` : ''}
                size={'xs'}
                color={'light'}
                fontType={'light'}
            />
            <Typography
                className={'input-container__error-label mt-2'}
                show={(error && error.length > 0) || false}
                text={error}
                display={'block'}
                size={'xs'}
                color={'danger'}
                fontType={'medium'}
            />
            {isOptional && !error && !viewOnly && (
                <Typography
                    size={'sm'}
                    color={'light'}
                    className={'input-container__optional ml-3 mt-2 mb-3'}
                    text={strings.OPTIONAL}
                    isItalic
                />
            )}
        </div>
    )
}

InputField.propTypes = {
    /** @param label {String} -> label that represents the Input Field component */
    label: PropTypes.string,

    /** @param value {*} -> value prop for input element */
    value: PropTypes.any,

    /** @param inputProps {Object} -> (start | end) object which positions the icon inside input component */
    inputProps: PropTypes.shape({
        start: PropTypes.func,
        end: PropTypes.func,
    }),

    /** @param size {String} -> ( 'xs'| 'xss' | sm | 'md' | 'lg' | 'xl' ) different sizes */
    size: PropTypes.oneOf(['md', 'lg', 'xl', 'sm', 'xs', 'xxs']),

    /** @param error {String} -> */
    error: PropTypes.string,

    /** @param className {String} -> overrides the default input component styles */
    className: PropTypes.string,
    rest: PropTypes.object,
}

export default memo(InputField)
